import React from 'react';
import { FlexFill, FooterWrapper, Title } from '../utils/styled';
import { Button, Modal, message } from 'antd';
import { tid } from '@utils/test';
import useDeleteSavedDashboard from '../useDeleteSavedDashboard';
import { useDispatch } from 'react-redux';
import { getList } from '@store/dashboards/list/actions';
import * as tempDashboardActions from '@store/tempDashboard/actions';
import { track } from '@utils';
import { CANCEL_DELETE_DASHBOARD_POPUP } from '@src/utils/sensor/track';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultLandingPageMutation } from '@hooks/useDefaultLandingPage';
import { SAVED_DASHBOARD_PATHS } from '@utils/constants';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { homePagePath } from '@src/routers/utils';

interface DeleteSavedDashboardPopupProps {
  isPopupOpen: boolean;
  closePopup: () => void;
  dashboardId: string;
  onDeleteCallback?: (() => void) | null;
  goToHome?: boolean;
  dashboardTypeName: string;
  isDefaultLandingPageOverride?: boolean;
}

function DeleteSavedDashboardPopup({
  isPopupOpen,
  closePopup,
  dashboardId,
  onDeleteCallback = null,
  goToHome = true,
  dashboardTypeName,
  isDefaultLandingPageOverride,
}: DeleteSavedDashboardPopupProps) {
  const { baseUrl } = window.conviva;
  const { deleteSDMutation } = useDeleteSavedDashboard();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const displaySuccessMessage = () => {
    message.success('Successfully Deleted!', 2);
  };

  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    name: state.name,
    path: state.path,
  }));

  // Use the mutation hook for API calls
  const { setDefaultLandingPage: mutateLandingPage } = useDefaultLandingPageMutation();

  const urlPrefix = `${baseUrl}`;
  const nameInUrl = get(SAVED_DASHBOARD_PATHS, [dashboardTypeName], '');
  const pathnameOfDashboard = nameInUrl
    ? `${urlPrefix}/dashboard/${nameInUrl}/${dashboardId}`
    : undefined;

  // Use override if provided (from saved dashboards list), otherwise calculate from current URL
  const isDefaultLandingPageCalculated = pathnameOfDashboard
    ? (defaultLandingPage.url || '').endsWith(pathnameOfDashboard)
    : false;
  const isDefaultLandingPage = isDefaultLandingPageOverride ?? isDefaultLandingPageCalculated;

  const callDeleteSavedDashboardHandler = async () => {
    try {
      if (isDefaultLandingPage) {
        const payload = {
          url: '',
          path: '',
          name: '',
        };

        // Use the mutation hook which handles both API call and local state update
        mutateLandingPage(payload);
      }

      await deleteSDMutation.mutateAsync(dashboardId);

      closePopup();
      displaySuccessMessage();
      dispatch(getList(null)); // Pass null as id parameter to get all dashboards
      dispatch(tempDashboardActions.remove(dashboardId, true));

      // TODO: only applicable for dashboard view, skip for options workflow
      onDeleteCallback && typeof onDeleteCallback === 'function' && onDeleteCallback();

      if (goToHome) {
        navigate(homePagePath, { replace: true });
      }
    } catch (error) {
      // console.log('psd error', error);
      // sd-TODO: handle the error in deleting the dashboard
    }
  };

  const onCancelPopup = () => {
    closePopup();
    track({
      event: CANCEL_DELETE_DASHBOARD_POPUP,
    });
  };

  const modalTitle = <Title>Delete Dashboard</Title>;

  const footerButtons = [
    <FooterWrapper key="sd-popup-footer-wrapper" {...tid('delete-sd-popup-footer')}>
      <FlexFill />
      <Button key="sd-popup-cancel" onClick={closePopup}>
        Cancel
      </Button>
      <Button type="primary" key="sd-popup-delete" onClick={callDeleteSavedDashboardHandler}>
        OK
      </Button>
    </FooterWrapper>,
  ];

  return (
    <Modal
      title={modalTitle}
      width={600}
      open={isPopupOpen}
      okText="Save"
      cancelText="Cancel"
      footer={footerButtons}
      maskClosable={true}
      onCancel={onCancelPopup}
    >
      <p>Are you sure you want to delete the dashboard?</p>
      {isDefaultLandingPage ? (
        <p>
          <strong>Note:</strong> This dashboard is set as your default landing page. If deleted,
          your default landing page will reset to Conviva default.
        </p>
      ) : null}
    </Modal>
  );
}

export default DeleteSavedDashboardPopup;
